import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Code2, Key, Copy, Check, BookOpen, Zap, Terminal, FileVideo, Image as ImageIcon,
  Sparkles, Users, ChevronRight,
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL || "https://mediaothers.com";

const SECTIONS = [
  { id: "auth",       label: "Authentication", icon: Key },
  { id: "projects",   label: "Projects",       icon: FileVideo },
  { id: "photos",     label: "Photos",         icon: ImageIcon },
  { id: "generate",   label: "Generate video", icon: Zap },
  { id: "ai",         label: "AI content",     icon: Sparkles },
  { id: "brand-kit",  label: "Brand kit",      icon: BookOpen },
  { id: "workspaces", label: "Workspaces",     icon: Users },
];

const ENDPOINTS = {
  auth: [
    {
      method: "POST", path: "/api/auth/login", auth: "none",
      description: "Exchange email + password for a JWT session token (2-week expiry).",
      body: { email: "you@company.com", password: "your-password" },
      response: { token: "eyJhbGci…", user: { id: "usr_…", email: "you@company.com", tier: "studio" } },
    },
    {
      method: "GET", path: "/api/auth/me", auth: "bearer",
      description: "Return the currently authenticated user profile. Works with JWT and sk_mo_ API keys.",
      response: { id: "usr_…", email: "you@company.com", tier: "studio", role: "customer" },
    },
    {
      method: "POST", path: "/api/keys", auth: "bearer (browser session only)",
      description: "Mint a new sk_mo_ API key. Raw value returned ONCE — cannot be recovered later. Studio+ only.",
      body: { name: "Production server" },
      response: { id: "key_…", name: "Production server", prefix: "sk_mo_ab12", raw_key: "sk_mo_...", created_at: "2026-02-20T…" },
    },
  ],
  projects: [
    {
      method: "GET", path: "/api/projects", auth: "bearer",
      description: "List all projects in the caller's active workspace, newest first.",
      response: [{ id: "proj_…", title: "Villa · Amsterdam", status: "completed", photos: [], settings: { aspect: "16:9" } }],
    },
    {
      method: "POST", path: "/api/projects", auth: "bearer",
      description: "Create a new draft project. Photos and settings are edited afterwards.",
      body: { name: "Villa · Amsterdam", settings: { aspect: "16:9", resolution: "1080p", provider_id: "auto", auto_tier: "best_value" } },
      response: { id: "proj_…", status: "draft" },
    },
    {
      method: "GET", path: "/api/projects/{id}", auth: "bearer",
      description: "Fetch a single project including photos, settings, status and (if completed) video_filename.",
      response: { id: "proj_…", status: "completed", video_filename: "abc.mp4", credits_used: 60 },
    },
    {
      method: "PATCH", path: "/api/projects/{id}", auth: "bearer",
      description: "Update project fields (title, settings, script, captions, etc). Partial payload accepted.",
      body: { settings: { provider_id: "premium-tier" } },
    },
    {
      method: "DELETE", path: "/api/projects/{id}", auth: "bearer",
      description: "Delete the project and its uploaded photos + generated video.",
    },
  ],
  photos: [
    {
      method: "POST", path: "/api/projects/{id}/photos", auth: "bearer",
      description: "Upload one or more photos (multipart/form-data field name: files — repeatable). JPG/PNG/HEIC supported, ~15 MB each.",
      body: "multipart/form-data → files=@villa1.jpg files=@villa2.jpg",
      response: [{ filename: "abc123.jpg", url: "/api/projects/{id}/photos/abc123.jpg" }],
    },
    {
      method: "DELETE", path: "/api/projects/{id}/photos/{filename}", auth: "bearer",
      description: "Remove a single uploaded photo from the project.",
    },
  ],
  generate: [
    {
      method: "POST", path: "/api/projects/{id}/cost", auth: "bearer",
      description: "Compute credit cost for the current provider/tier + photo count BEFORE generating.",
      response: { provider_id: "kling-25-turbo", provider_name: "Kling 2.5 Turbo", credits_per_clip: 35, num_photos: 6, total_credits: 210 },
    },
    {
      method: "POST", path: "/api/projects/{id}/generate", auth: "bearer",
      description: "Start video generation. Deducts credits and enqueues the job. Poll /api/projects/{id} for status.",
      response: { status: "queued", credits_used: 210, provider_used: "kling-25-turbo", selection_key: "premium" },
    },
    {
      method: "GET", path: "/api/videos/{filename}", auth: "bearer",
      description: "Stream / download the finished MP4 (or use HTTP Range for partial downloads).",
    },
  ],
  ai: [
    {
      method: "POST", path: "/api/ai/blog", auth: "bearer",
      description: "Generate a full SEO blog post. Studio+ Brand Kit is auto-injected when set.",
      body: { topic: "How to prep a home for sale", niche: "real-estate", tone: "friendly", language: "en", length: "medium" },
      response: { title: "…", sections: [{ heading: "…", body: "…" }], meta_description: "…", tags: [], brand_kit_applied: true },
    },
    {
      method: "POST", path: "/api/ai/social-post", auth: "bearer",
      description: "Generate a platform-native social post with 3 alternative hooks. Cost: 3 credits.",
      body: { topic: "New listing tour", platform: "instagram", tone: "engaging", language: "en" },
      response: { main: "…", hooks: ["…"], hashtags: ["realestate"], cta: "DM to visit" },
    },
    {
      method: "POST", path: "/api/ai/translate", auth: "bearer",
      description: "Translate arbitrary text to another language. Cost: 2 credits.",
      body: { text: "Hello world", target_language: "nl" },
      response: { translated: "Hallo wereld", target_language: "nl" },
    },
    {
      method: "POST", path: "/api/ai/series/generate", auth: "bearer",
      description: "Bulk-generate a content series (3-8 posts) around a single theme. Cost: 3 credits per post.",
      body: { theme: "Amsterdam apartment", platform: "instagram", count: 5, tone: "informative", language: "en" },
    },
  ],
  "brand-kit": [
    {
      method: "POST", path: "/api/brand-kit/scan-website", auth: "bearer",
      description: "One-click scan: reads your homepage, distills tagline / tone / summary, auto-saves to the active workspace. Studio+ · 5 credits.",
      body: { url: "https://yourcompany.com" },
      response: { workspace_id: "ws_…", brand_tagline: "…", brand_tone: "…", brand_summary: "…" },
    },
    {
      method: "PATCH", path: "/api/workspaces/{id}", auth: "bearer",
      description: "Manually set/update workspace Brand Kit fields.",
      body: { brand_tagline: "…", brand_tone: "warm, direct", brand_summary: "Who we are and who we serve." },
    },
  ],
  workspaces: [
    { method: "GET",  path: "/api/workspaces", auth: "bearer", description: "List all workspaces you own." },
    { method: "POST", path: "/api/workspaces", auth: "bearer", description: "Create a new workspace.", body: { name: "Second brand", icon: "◇" } },
    { method: "POST", path: "/api/workspaces/{id}/invite", auth: "bearer", description: "Invite a teammate. Studio+ · uses seats.", body: { email: "teammate@company.com", role: "editor" } },
    { method: "GET",  path: "/api/workspaces/{id}/members", auth: "bearer", description: "List active + pending members." },
  ],
};

export default function DocsApi() {
  const [active, setActive] = useState("auth");

  return (
    <div className="min-h-screen bg-[#0b0b0d] text-white">
      {/* Header */}
      <div className="border-b border-white/5 bg-gradient-to-b from-amber-500/5 to-transparent">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-10">
          <Link to="/" className="font-mono-tech text-[10px] text-zinc-500 hover:text-amber-400" data-testid="docs-back-home">
            ← MEDIAOTHERS · HOME
          </Link>
          <div className="mt-6 flex items-center gap-3">
            <Terminal className="w-6 h-6 text-amber-400" strokeWidth={1.5} />
            <div className="font-mono-tech text-[10px] text-amber-400">DEVELOPER · DOCS</div>
          </div>
          <h1 className="mt-2 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-none">
            The <span className="font-bold text-amber-400">MediaOthers</span> API.
          </h1>
          <p className="text-zinc-400 mt-5 max-w-2xl">
            Build with cinematic AI video, on-brand blog posts, social copy in 12 languages and bulk-scheduling —
            straight from your own tools. REST, JSON, Bearer tokens. No SDK required.
          </p>
          <div className="mt-6 flex items-center gap-3 flex-wrap" data-testid="docs-cta-row">
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 font-mono-tech text-[10px] uppercase">
              STUDIO+ · required
            </Badge>
            <span className="text-xs text-zinc-500">·</span>
            <Link to="/pricing" className="text-xs font-mono-tech uppercase text-amber-400 hover:text-amber-300" data-testid="docs-pricing-link">
              View plans →
            </Link>
            <span className="text-xs text-zinc-500">·</span>
            <Link to="/settings" className="text-xs font-mono-tech uppercase text-amber-400 hover:text-amber-300" data-testid="docs-mint-key-link">
              Mint an API key →
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-10 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8">
        {/* Left nav */}
        <nav className="md:sticky md:top-6 md:self-start space-y-1" data-testid="docs-nav">
          <div className="font-mono-tech text-[10px] text-zinc-500 mb-3 uppercase tracking-wider">Endpoints</div>
          {SECTIONS.map((s) => {
            const Ico = s.icon;
            const isActive = active === s.id;
            return (
              <button
                key={s.id}
                data-testid={`docs-nav-${s.id}`}
                onClick={() => {
                  setActive(s.id);
                  document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded text-left text-sm transition-colors ${
                  isActive ? "bg-amber-500/10 text-amber-300" : "text-zinc-400 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Ico className="w-3.5 h-3.5" strokeWidth={1.75} />
                {s.label}
                <ChevronRight className={`ml-auto w-3 h-3 opacity-0 ${isActive ? "opacity-100" : ""}`} strokeWidth={2} />
              </button>
            );
          })}
          <div className="mt-6 p-3 rounded border border-white/5 bg-[#121214]">
            <div className="font-mono-tech text-[9px] text-zinc-500 mb-1">BASE · URL</div>
            <code className="text-xs text-amber-300 break-all font-mono">{BASE}</code>
          </div>
        </nav>

        {/* Content */}
        <div className="space-y-12">
          {/* Quick start */}
          <section data-testid="docs-quickstart">
            <div className="font-mono-tech text-[10px] text-amber-400 mb-2">QUICK · START</div>
            <h2 className="font-heading text-2xl font-bold tracking-tight mb-4">Three lines to your first video.</h2>
            <CodeBlock
              testid="quickstart-code"
              language="bash"
              code={`# 1) Auth check
curl -H "Authorization: Bearer sk_mo_..." ${BASE}/api/auth/me

# 2) Create a project
curl -X POST ${BASE}/api/projects \\
  -H "Authorization: Bearer sk_mo_..." \\
  -H "Content-Type: application/json" \\
  -d '{"name":"Villa · Amsterdam","settings":{"aspect":"16:9","provider_id":"auto","auto_tier":"best_value"}}'

# 3) Upload photos then generate — see /generate section below.`}
            />
          </section>

          {SECTIONS.map((s) => (
            <section key={s.id} id={`section-${s.id}`} data-testid={`section-${s.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <s.icon className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
                <div className="font-mono-tech text-[10px] text-amber-400 uppercase tracking-wider">{s.label}</div>
              </div>
              <h2 className="font-heading text-2xl font-bold tracking-tight mb-6">{s.label} endpoints</h2>
              <div className="space-y-6">
                {ENDPOINTS[s.id].map((ep, idx) => (
                  <EndpointCard key={idx} ep={ep} />
                ))}
              </div>
            </section>
          ))}

          {/* Errors */}
          <section data-testid="docs-errors">
            <div className="font-mono-tech text-[10px] text-amber-400 mb-2">ERROR · CODES</div>
            <h2 className="font-heading text-2xl font-bold tracking-tight mb-4">HTTP conventions.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["200", "OK — request succeeded"],
                ["400", "Bad request — malformed body or invalid parameter"],
                ["401", "Missing / invalid / revoked bearer token"],
                ["402", "Not enough credits, or plan gate blocks this endpoint"],
                ["403", "Authenticated but not allowed (e.g. cross-user, API-key privilege isolation)"],
                ["404", "Resource not found"],
                ["429", "Rate limited — back off + retry with jitter"],
                ["500", "Server error — safe to retry"],
              ].map(([code, txt]) => (
                <div key={code} className="p-3 rounded border border-white/5 bg-[#121214] flex items-start gap-3">
                  <div className="font-mono-tech text-amber-400 text-xs font-bold w-9">{code}</div>
                  <div className="text-xs text-zinc-400">{txt}</div>
                </div>
              ))}
            </div>
          </section>

          <div className="pt-8 border-t border-white/5 text-xs text-zinc-500">
            Need help? Email <a href="mailto:sales@mediaothers.com" className="text-amber-400 hover:text-amber-300">sales@mediaothers.com</a>.
            Studio + Enterprise plans include priority support.
          </div>
        </div>
      </div>
    </div>
  );
}

function EndpointCard({ ep }) {
  const methodColor = {
    GET:    "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    POST:   "bg-amber-500/15 text-amber-300 border-amber-500/30",
    PATCH:  "bg-sky-500/15 text-sky-300 border-sky-500/30",
    DELETE: "bg-red-500/15 text-red-300 border-red-500/30",
  }[ep.method] || "bg-white/10 text-white border-white/20";

  const curl = buildCurl(ep);

  return (
    <div className="border border-white/5 rounded-lg bg-[#121214] overflow-hidden" data-testid={`endpoint-${ep.method.toLowerCase()}-${ep.path.replace(/[^a-z0-9]/gi, "-")}`}>
      <div className="p-4 flex items-center gap-3 flex-wrap border-b border-white/5">
        <span className={`font-mono-tech text-[10px] font-bold px-2 py-1 rounded border ${methodColor}`}>{ep.method}</span>
        <code className="font-mono text-sm text-white break-all normal-case tracking-normal">{ep.path}</code>
        <Badge className="ml-auto bg-white/5 text-zinc-400 border-white/10 font-mono-tech text-[9px] uppercase">
          {ep.auth}
        </Badge>
      </div>
      <div className="p-4 space-y-4">
        <p className="text-sm text-zinc-300">{ep.description}</p>
        {ep.body && (
          <div>
            <div className="font-mono-tech text-[10px] text-zinc-500 mb-2">REQUEST · BODY</div>
            <CodeBlock language="json" code={typeof ep.body === "string" ? ep.body : JSON.stringify(ep.body, null, 2)} />
          </div>
        )}
        {ep.response && (
          <div>
            <div className="font-mono-tech text-[10px] text-zinc-500 mb-2">RESPONSE · EXAMPLE</div>
            <CodeBlock language="json" code={JSON.stringify(ep.response, null, 2)} />
          </div>
        )}
        <div>
          <div className="font-mono-tech text-[10px] text-zinc-500 mb-2">CURL</div>
          <CodeBlock language="bash" code={curl} />
        </div>
      </div>
    </div>
  );
}

function CodeBlock({ code, language, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true); toast.success("Copied");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Could not copy"); }
  };
  return (
    <div className="relative bg-[#0b0b0d] border border-white/5 rounded-md" data-testid={testid}>
      <button
        onClick={copy}
        className="absolute top-2 right-2 p-1.5 rounded text-zinc-400 hover:text-amber-300 hover:bg-white/5"
        title="Copy"
      >
        {copied ? <Check className="w-3.5 h-3.5" strokeWidth={2} /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.75} />}
      </button>
      <pre className="p-4 text-xs text-zinc-200 overflow-x-auto font-mono normal-case tracking-normal">
        <code>{code}</code>
      </pre>
      {language && <div className="absolute bottom-2 right-2 font-mono-tech text-[9px] text-zinc-600 uppercase">{language}</div>}
    </div>
  );
}

function buildCurl(ep) {
  const parts = [];
  const method = ep.method === "GET" ? "" : ` -X ${ep.method}`;
  parts.push(`curl${method} ${BASE}${ep.path}`);
  parts.push(`  -H "Authorization: Bearer sk_mo_..."`);
  if (ep.body && typeof ep.body === "object") {
    parts.push(`  -H "Content-Type: application/json"`);
    parts.push(`  -d '${JSON.stringify(ep.body)}'`);
  } else if (typeof ep.body === "string") {
    parts.push(`  ${ep.body.startsWith("multipart") ? `-F "files=@villa1.jpg" -F "files=@villa2.jpg"` : `-d '${ep.body}'`}`);
  }
  return parts.join(" \\\n");
}
